import { Fragment, useEffect, useRef, useState } from 'react';
import { Field, Form, withFormik } from 'formik';
import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import { kebabCase } from 'lodash';

import API from 'utils/api';
import {
  gaTrackBlogSubscriptions,
  gaTrackDownloads,
} from 'utils/google-analytics/gaTracking';
import windowCheck from 'utils/helpers/window-check';
import { params as parseParams } from 'utils/mercury/partials/params';
import trackMercuryEvent from 'utils/mercury/trackMercuryEvent';

import FontIcon from 'components/icons';

import './styles/crm-form.scss';
import Button from '../buttons/button';
import SignupResponseError from '../modal/signup-response-error';
import PhoneField from './fields/phone';

const fullNameRegExp = /^[A-Za-z][A-Za-z\'\-\.\ é\,]+([\ A-Za-z][A-Za-z\'\-\.\ é\,]+)*$/;
const phoneRegExp = /^\+?(\d{2})\)?[-. ]?(\d{4})[-. ]?(\d{4})$/;
const emailRegExp = /[a-z0-9!#$%&'*+/=?^_`{|} ~-]+(?: \.[a - z0 - 9!#$ %& '*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
const messageRegExp = /\w+\s\w+/;

const CRMForm = props => {
  const firstName = props.firstName || '';
  const lastName = props.lastName || '';
  const email = props.email || '';
  const fullNameEmailGrouped = props.fullNameEmailGrouped || '';
  const fields = {
    fullName: props.fullNameField || false,
    email: props.emailField || false,
    phone: props.phoneField || false,
    industry: props.industryField || false,
    employees: props.employeesField || false,
    message: props.messageField || false,
  };
  const submitBtnText = props.submitBtnText || '';
  const fieldLayout = props.fieldLayout || '';
  const showFields = props.showInitFields;
  const showIcons = props.showIcons || '';
  const formType = props.formType || '';
  const responseCode = props.responseCode || '';
  const targetModule = props.targetModule || '';
  const uniqueId = props.uniqueId || 'crmForm';
  const [responseNumber, setResponseNumber] = useState(0);
  const [hideErrorMessage, setHideErrorMessage] = useState(true);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [onSuccess, setOnSuccess] = useState(false);
  const [contentTitle, setContentTitle] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const updateSuccess = props.updateSuccess || false;

  useEffect(() => {
    if (
      formType === 'contentDownload' ||
      formType === 'HWIcontentDownload' ||
      formType === 'freeTimeCardCalculatorDownload'
    ) {
      try {
        if (props.downloadUrl === '' || props.downloadUrl === '#')
          throw 'content download URL is empty';
        if (props.downloadUrl === null) throw 'content download URL is null';
        if (!props.downloadUrl) throw 'content download URL doesn\'t exist';
        setDownloadUrl(props.downloadUrl);
        if (props.contentTitle === '' || props.contentTitle === '#')
          throw 'content asset title is empty';
        if (props.contentTitle === null) throw 'content asset title is null';
        if (!props.contentTitle) throw 'content asset title doesn\'t exist';
        setContentTitle(props.contentTitle);
      } catch (err) {
        console.log(`CRM Form Error: ${err}`);
      }
    }
  }, []);

  const closeErrResponse = () => {
    setResponseNumber(0);
    setCreatingAccount(false);
  };
  const data = useStaticQuery(graphql`
    {
      allWordpressAcfOptions {
        edges {
          node {
            options {
              dynamic_text_block {
                copy
                title
              }
            }
          }
        }
      }
    }
  `);

  const dataContext =
    data.allWordpressAcfOptions.edges[0].node.options.dynamic_text_block;
  const [successMessage, setSuccessMessage] = useState();

  useEffect(() => {
    switch (formType) {
      case 'blogSubscription':
        setSuccessMessage(dataContext[0].copy);
        break;
      case 'newsletterSubscription':
        setSuccessMessage(dataContext[1].copy);
        break;
      case 'contentDownload':
        setSuccessMessage(dataContext[2].copy);
        break;
      case 'HWIcontentDownload':
        setSuccessMessage(dataContext[2].copy);
        break;
      case 'requestInfo':
        setSuccessMessage(dataContext[3].copy);
        break;
      case 'FOWSubscription':
        setSuccessMessage(dataContext[4].copy);
        break;
      case 'eventSubscription':
        setSuccessMessage(dataContext[5].copy);
        break;
      case 'freeTimeCardCalculatorDownload':
        setSuccessMessage(dataContext[6].copy);
        break;
    }
  }, [formType]);

  return (
    <div className="crm-form is-fullwidth">
      <FormikSchedulingSignup
        validateOnBlur={ false }
        validateOnChange={ false }
        firstName={ firstName }
        lastName={ lastName }
        email={ email }
        submitBtnText={ submitBtnText }
        fieldLayout={ fieldLayout }
        showInitFields={ showFields }
        showIcons={ showIcons }
        fields={ fields }
        fullNameEmailGrouped={ fullNameEmailGrouped }
        formType={ formType }
        contentTitle={ contentTitle }
        downloadUrl={ downloadUrl }
        responseCode={ responseCode }
        hideErrorMessage={ hideErrorMessage }
        setHideErrorMessage={ setHideErrorMessage }
        updateResponse={ setResponseNumber }
        onSuccess={ onSuccess }
        updateSuccess={ updateSuccess }
        setOnSuccess={ setOnSuccess }
        updateCreatingAccount={ setCreatingAccount }
        creatingAccount={ creatingAccount }
        targetModule={ targetModule }
        successMessage={ successMessage }
        uniqueId={ uniqueId }
      />
      <SignupResponseError
        closeErrResponse={ closeErrResponse }
        responseCode={ responseNumber }
        className={ responseNumber > 0 ? 'is-block' : 'is-hidden' }
      />
    </div>
  );
};

// Form
const CRMEmailSignupForm = ({
  values,
  errors,
  touched,
  setErrors,
  setHideErrorMessage,
  hideErrorMessage,
  submitBtnText,
  formType,
  contentTitle,
  downloadUrl,
  handleSubmit,
  showInitFields,
  showIcons,
  fields,
  fullNameEmailGrouped,
  creatingAccount,
  onSuccess,
  successMessage,
  uniqueId,
  updateSuccess,
  fieldLayout,
  ...props
}) => {
  const [highlightInput, updateHighlightInput] = useState(true);
  const focusTimerRef = useRef(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // if there are errors show them
      props.updateCreatingAccount(false);
      setHideErrorMessage(false);
      updateHighlightInput(false);
      if (
        Object.keys(errors)[0] === 'phone'
      ) {
        //this is her because you can't pass any props to change the id down to the phoneComponent through formik
        document.getElementById(Object.keys(errors)[0]).focus();
      } else {
        document.getElementById(`${Object.keys(errors)[0]}${uniqueId}`).focus();
      }
      setTimer();
    }
  }, [errors]);

  useEffect(() => {
    setHideErrorMessage(true); // on input value change hide the error message
    updateHighlightInput(true); // on input value change hide the red outline
    stopTimer();

    emailRegExp.test(values.email) && errors.email ? setErrors({}) : '';
    fullNameRegExp.test(values.fullName) && errors.fullName
      ? setErrors({})
      : '';
    phoneRegExp.test(values.phone) && errors.phone ? setErrors({}) : '';
    values.industry_id && errors.industry_id ? setErrors({}) : '';
    values.ref_employees && errors.ref_employees ? setErrors({}) : '';
    messageRegExp.test(values.message) && errors.message ? setErrors({}) : '';
  }, [values]);

  function setTimer() {
    if (Object.keys(errors).length > 0) {
      focusTimerRef.current = setTimeout(() => {
        setHideErrorMessage(true);
      }, 3000);
    }
  }

  function setTimerOnClick() {
    formType === 'newsletterSubscription' ||
    formType === 'blogSubscription' ||
    formType === 'eventSubscription' ||
    formType === 'FOWSubscription'
      ? handleSubmit
      : '';
    props.updateCreatingAccount(true);
    if (focusTimerRef.current) {
      stopTimer();
    }
    if (Object.keys(errors).length > 0 && props.submitCount > 0) {
      // on all submissions after the first one if there are errors show them on click
      setHideErrorMessage(false);
      updateHighlightInput(false);
      if (
        Object.keys(errors)[0] === 'phone'
      ) {
        document.getElementById(Object.keys(errors)[0])?.focus();
      } else {
        document
          .getElementById(`${Object.keys(errors)[0]}${uniqueId}`)
          ?.focus();
      }
      setTimer();
      props.updateCreatingAccount(false);
    }
  }

  function blurHandler() {
    // on blur of input with current error message hide message
    setHideErrorMessage(true);
    updateHighlightInput(true);
    if (focusTimerRef.current) {
      stopTimer();
    }
  }

  function stopTimer() {
    clearTimeout(focusTimerRef.current);
  }

  const showIconsCheck = showIcons === true ? '' : 'is-hidden';
  const singleEmailLayout =
    formType === 'newsletterSubscription' ||
    formType === 'blogSubscription' ||
    formType === 'eventSubscription' ||
    formType === 'FOWSubscription' ||
    formType === 'freeTimeCardCalculatorDownload';
  return (
    <div className={ `crm-form-container ${showIcons ? 'show-icons' : ''}` }>
      {!onSuccess ? (
        <Form
          noValidate
          className={ `${formType &&
            kebabCase(formType)} ${fullNameEmailGrouped &&
            'fullname-email-grouped'}` }
        >
          <input type="hidden" name="oid" value="00Do0000000dBgo" />
          <input type="hidden" name="Campaign_ID" value="7012G000001dXJjQAM" />

          <div
            className={ `field-wrapper ${fullNameEmailGrouped &&
              'field-body fullname-email-wrapper'} ${
              !!fields.fullName && fields.email ? 'is-ungrouped' : ''
            }` }
          >
            {fields.fullName && (
              <div className="field field-fullname">
                <label htmlFor={ `fullName${uniqueId}` } className="is-sr-only">
                  Full Name
                </label>
                <div className={ `control ${showInitFields ? 'is-hidden' : ''}` }>
                  <Field
                    className="input"
                    id={ `fullName${uniqueId}` }
                    type="text"
                    title="Full Name"
                    aria-placeholder="Please enter your first and last name"
                    aria-required="true"
                    name="fullName"
                    onBlur={ blurHandler }
                    placeholder="First & Last Name"
                    style={
                      touched.fullName && errors.fullName
                        ? { border: '2px solid red' }
                        : null
                    }
                  />
                  <span
                    className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                  >
                    <FontIcon icon="person" className="icon-user" />
                  </span>
                  {touched.fullName && errors.fullName && (
                    <div
                      className={ `errors-tooltip has-icons-left ${
                        document.activeElement.id !== `fullName${uniqueId}` || // if the current focused element id is anything other than 'fullName' or if hideErrorState is true then hide the error message
                        hideErrorMessage === true
                          ? 'is-hidden'
                          : ''
                      }` }
                    >
                      <p className="tooltip-inner has-text-danger">
                        <span className="has-text-warning tooltip-icon">
                          <FontIcon
                            icon="warning"
                            className="icon-exclamation"
                          />
                        </span>
                        <span className="tooltip-text">{errors.fullName}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {fields.email && (
              <div
                className={ `field field-email ${singleEmailLayout &&
                  'is-single-field'} ${
                  singleEmailLayout && fieldLayout !== 'row'
                    ? 'field has-addons'
                    : ''
                }` }
              >
                <Field
                  name="user"
                  type="hidden"
                  value="look in handle submit"
                />
                <Field
                  name="redirect_on_success"
                  type="hidden"
                  value="/thanks"
                />
                <label htmlFor={ `email${uniqueId}` } className="is-sr-only">
                  Email
                </label>
                <div
                  className={ `control has-input ${
                    showInitFields ? 'is-hidden' : ''
                  }` }
                >
                  <Field
                    className={ `input email ${fieldLayout}` }
                    id={ `email${uniqueId}` }
                    title="Email Address"
                    aria-placeholder="Please enter your email address"
                    aria-required="true"
                    type="email"
                    name="email"
                    onBlur={ blurHandler }
                    placeholder="What is your best email?"
                    style={
                      touched.email && errors.email
                        ? { border: '2px solid red' }
                        : null
                    }
                  />
                  <span
                    className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                  >
                    <FontIcon icon="email" className="icon-envelope" />
                  </span>
                  {touched.email && errors.email && (
                    <div
                      className={ `errors-tooltip ${
                        document.activeElement.id !== `email${uniqueId}` ||
                        hideErrorMessage === true
                          ? 'is-hidden'
                          : ''
                      }` }
                    >
                      <p className="tooltip-inner has-text-danger">
                        <span className="has-text-warning tooltip-icon">
                          <FontIcon
                            icon="warning"
                            className="icon-exclamation"
                          />
                        </span>
                        <span className="tooltip-text">{errors.email}</span>
                      </p>
                    </div>
                  )}
                </div>
                {singleEmailLayout && fieldLayout !== 'row' && (
                  <div className="control">
                    <button
                      type="submit"
                      aria-label="Form Submit"
                      title="Submit Email"
                      className="button-subscribe"
                      onClick={ setTimerOnClick }
                    >
                      <span>
                        <FontIcon
                          icon="chevron-right"
                          className="icon-chevron-right"
                          fill="white"
                        />
                      </span>
                    </button>
                  </div>
                )}
                {singleEmailLayout && fieldLayout === 'row' && (
                  <div className="control">
                    <button
                      type="submit"
                      title="Submit Form"
                      aria-label="Form Submit"
                      className="button-subscribe"
                      onClick={ setTimerOnClick }
                    >
                      {Boolean(submitBtnText) ? submitBtnText : 'Sign Me Up!'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {fields.phone && (
            <div className="field field-phone">
              <label htmlFor="phone" className="is-sr-only">
                Phone
              </label>
              <div className="control has-icons-left">
                <Field
                  className="input phone"
                  component={ PhoneField }
                  title="Phone Number"
                  aria-placeholder="Please enter your phone number"
                  aria-required="true"
                  type="tel"
                  name="phone"
                  id="phone"
                  onBlur={ blurHandler }
                  placeholder="What is your mobile number? (for text alerts)"
                  style={
                    touched.phone && errors.phone
                      ? { border: '2px solid red', borderRadius: '4px' }
                      : null
                  }
                />
                <span
                  className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                >
                  <FontIcon icon="phone" className="icon-phone" />
                </span>
                {touched.phone && errors.phone && (
                  <div
                    className={ `errors - tooltip ${
                      document.activeElement.id !== 'phone' ||
                      hideErrorMessage === true
                        ? 'is-hidden'
                        : ''
                    } ` }
                  >
                    <p className="tooltip-inner has-text-danger">
                      <span className="has-text-warning tooltip-icon">
                        <FontIcon
                          icon="warning"
                          className="icon-exclamation"
                        />
                      </span>
                      <span className="tooltip-text">{errors.phone}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {fields.industry && (
            <div className="field field-industry">
              <label htmlFor={ `industry_id${uniqueId}` } className="is-sr-only">
                What best describes your industry?
              </label>
              <div className="control has-icons-left">
                <div className="select is-full-width">
                  <Field
                    as="select"
                    className="input select-field is-fullwidth"
                    component="select"
                    title="Industry Selection"
                    aria-placeholder="Please select an industry title which best describes your industry"
                    id={ `industry_id${uniqueId}` }
                    type="select"
                    name="industry_id"
                    onBlur={ blurHandler }
                    placeholder="What best describes your industry?"
                    style={
                      touched.industry_id && errors.industry_id
                        ? { border: '2px solid red' }
                        : null
                    }
                  >
                    <option default value="0">
                      What best describes your industry?
                    </option>
                    <option value="38">Call Center / Customer Support</option>
                    <option value="9">Education</option>
                    <option value="1">Food Services</option>
                    <option value="58">Government</option>
                    <option value="16">Healthcare</option>
                    <option value="8">Hospitality / Entertainment</option>
                    <option value="59">Logistics</option>
                    <option value="60">Manufacturing</option>
                    <option value="51">On-demand / Gig</option>
                    <option value="28">Retail</option>
                    <option value="15">Services</option>
                    <option value="48">Technology</option>
                    <option value="45">Something Else</option>
                  </Field>
                  <span
                    className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                  >
                    <FontIcon icon="building" />
                  </span>
                </div>
                {touched.industry_id && errors.industry_id && (
                  <div
                    className={ `errors-tooltip ${
                      document.activeElement.id !== `industry_id${uniqueId}` ||
                      hideErrorMessage === true
                        ? 'is-hidden'
                        : ''
                    }` }
                  >
                    <p className="tooltip-inner has-text-danger">
                      <span className="has-text-warning tooltip-icon">
                        <FontIcon icon="warning" />
                      </span>
                      <span className="tooltip-text">{errors.industry_id}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {fields.employees && (
            <div className="field">
              <label
                htmlFor={ `ref_employees${uniqueId}` }
                className="is-sr-only"
              >
                How many people do you schedule?
              </label>
              <div className="control has-icons-left">
                <div className="select is-full-width">
                  <Field
                    as="select"
                    className="input select-field is-fullwidth"
                    component="select"
                    title="Employee Selection"
                    aria-placeholder="Please select how many people you schedule"
                    aria-required="true"
                    id={ `ref_employees${uniqueId}` }
                    type="select"
                    name="ref_employees"
                    onBlur={ blurHandler }
                    placeholder="How many people do you schedule?"
                    style={
                      touched.ref_employees && errors.ref_employees
                        ? { border: '2px solid red' }
                        : null
                    }
                  >
                    <option default value="0">
                      How many people do you schedule?
                    </option>
                    <option value="5">1 - 5</option>
                    <option value="10">6 - 10</option>
                    <option value="20">11 - 20</option>
                    <option value="35">21 - 35</option>
                    <option value="50">36 - 50</option>
                    <option value="75">51 - 75</option>
                    <option value="100">76 - 100</option>
                    <option value="125">101 - 125</option>
                    <option value="250">126 - 250</option>
                    <option value="500">251 - 500</option>
                    <option value="501">501+</option>
                  </Field>
                  <span
                    className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                  >
                    <FontIcon icon="two-people" className="icon-users" />
                  </span>
                </div>
                {touched.ref_employees && errors.ref_employees && (
                  <div
                    className={ `errors-tooltip ${
                      document.activeElement.id !==
                        `ref_employees${uniqueId}` || hideErrorMessage === true
                        ? 'is-hidden'
                        : ''
                    }` }
                  >
                    <p className="tooltip-inner has-text-danger">
                      <span className="has-text-warning tooltip-icon">
                        <FontIcon
                          icon="warning"
                          className="icon-exclamation"
                        />
                      </span>
                      <span className="tooltip-text">
                        {errors.ref_employees}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {fields.message && (
            <div className="field field-message message-column">
              <label htmlFor={ `message${uniqueId}` } className="is-sr-only">
                Message
              </label>
              <div className="control has-icons-left">
                <Field
                  className="input message"
                  as="textarea"
                  type="text"
                  title="Message"
                  aria-placeholder="Please enter additional information in a message"
                  aria-required="true"
                  id={ `message${uniqueId}` }
                  name="message"
                  onBlur={ blurHandler }
                  placeholder="What can we help you with?"
                  style={
                    touched.message && errors.message
                      ? { border: '2px solid red' }
                      : null
                  }
                />
                <span
                  className={ `icon is-small is-left inputIcons ${showIconsCheck}` }
                >
                  <FontIcon icon="workchat" className="icon-question" />
                </span>
                {touched.message && errors.message && (
                  <div
                    className={ `errors-tooltip ${
                      document.activeElement.id !== `message${uniqueId}` ||
                      hideErrorMessage === true
                        ? 'is-hidden'
                        : ''
                    }` }
                  >
                    <p className="tooltip-inner has-text-danger">
                      <span className="has-text-warning tooltip-icon">
                        <FontIcon
                          icon="warning"
                          className="icon-exclamation"
                        />
                      </span>
                      <span className="tooltip-text">{errors.message}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {formType === 'subcribe' ? (
            ''
          ) : (
            <div
              className={ `field ${
                formType === 'newsletterSubscription' ||
                formType === 'blogSubscription' ||
                formType === 'eventSubscription' ||
                formType === 'FOWSubscription' ||
                formType === 'freeTimeCardCalculatorDownload'
                  ? 'is-hidden'
                  : ''
              }` }
            >
              <Field
                name="user"
                type="hidden"
                aria-hidden="true"
                value="look in handle submit"
              />
              <Field
                name="redirect_on_success"
                aria-hidden="true"
                type="hidden"
                value="/thank-you"
              />
              {formType === 'contentDownload' ||
              formType === 'freeTimeCardCalculatorDownload' ? (
                  <Fragment>
                    <Field
                      name="contentTitle"
                      aria-hidden="true"
                      type="hidden"
                      value={ contentTitle }
                    />
                    <Field
                      name="downloadUrl"
                      aria-hidden="true"
                      type="hidden"
                      value={ downloadUrl }
                    />
                  </Fragment>
                ) : (
                  ''
                )}
              {formType === 'HWIcontentDownload' ? (
                <Fragment>
                  <Field
                    name="contentTitle"
                    aria-hidden="true"
                    type="hidden"
                    value={ contentTitle }
                  />
                  <Field
                    name="downloadUrl"
                    aria-hidden="true"
                    type="hidden"
                    value={ downloadUrl }
                  />
                </Fragment>
              ) : (
                ''
              )}
              {formType === 'requestInfo' ? (
                <input
                  type="hidden"
                  aria-hidden="true"
                  name="debug"
                  value="1"
                />
              ) : (
                ''
              )}

              <Button
                className="button button-green is-fullwidth"
                type="submit"
                title="Form Submit"
                arial-label="Form Submit"
                text={ creatingAccount ? 'Please Wait' : submitBtnText }
                style={
                  creatingAccount
                    ? {
                      backgroundColor: '#d5d5d5',
                      color: 'rgba(51, 51, 51, .7)',
                    }
                    : null
                }
                onClick={ setTimerOnClick }
              />
            </div>
          )}
        </Form>
      ) : (
        <div
          className="success-message"
          dangerouslySetInnerHTML={ { __html: successMessage } }
        />
      )}
    </div>
  );
};

// Form Logic
const FormikSchedulingSignup = withFormik({
  mapPropsToValues({
    fullName,
    email,
    ref_employees,
    message,
    phone,
    redirect_on_success,
    user,
    ref_page,
    industry_id,
  }) {
    return {
      fullName: fullName || '',
      email: email || '',
      ref_employees: ref_employees || '',
      industry_id: industry_id || '',
      message: message || '',
      phone: phone || '',
      redirect_on_success: redirect_on_success || '',
      user: user || '',
      ref_page: ref_page || null,
    };
  },
  validate: (values, props) => {
    const errors = {};
    if (
      props.formType === 'newsletterSubscription' ||
      props.formType === 'blogSubscription' ||
      props.formType === 'eventSubscription' ||
      props.formType === 'FOWSubscription' ||
      props.formType === 'HWIcontentDownload' ||
      props.formType === 'freeTimeCardCalculatorDownload'
    ) {
      switch (true) {
        case !values.email || values.email === '':
          errors.email = 'Email Address Required';
          break;

        case !emailRegExp.test(values.email):
          errors.email = 'Invalid Email Address';
          break;
      }
    } else if (props.formType === 'contentDownload') {
      switch (true) {
        case !values.fullName || values.fullName === '':
          errors.fullName = 'Full Name Required';
          break;

        case !fullNameRegExp.test(values.fullName):
          errors.fullName = 'Invalid Full Name';
          break;

        case !values.email || values.email === '':
          errors.email = 'Email Address Required';
          break;

        case !emailRegExp.test(values.email):
          errors.email = 'Invalid Email Address';
          break;

        case !!props.fields.employees && !values.ref_employees:
          errors.ref_employees = 'Employees Are Required';
          break;
      }
    } else if (props.formType === 'requestInfo') {
      switch (true) {
        case !values.fullName || values.fullName === '':
          errors.fullName = 'Full Name Required';
          break;

        case !fullNameRegExp.test(values.fullName):
          errors.fullName = 'Invalid Full Name';
          break;

        case !values.email || values.email === '':
          errors.email = 'Email Address Required';
          break;

        case !emailRegExp.test(values.email):
          errors.email = 'Invalid Email Address';
          break;

        case !values.phone:
          errors.phone = 'Telephone Number Required';
          break;

        case !phoneRegExp.test(values.phone):
          errors.phone = 'Invalid Phone Number';
          break;

        case !values.ref_employees:
          errors.ref_employees = 'Employees Are Required';
          break;

        case !values.industry_id:
          errors.industry_id = 'Industry Description Required';
          break;

        case !messageRegExp.test(values.message):
          errors.message = 'Invalid Message';
          break;

        case !values.message || values.message === '':
          errors.message = 'A Message is Required';
          break;
      }
    }

    return errors;
  },
  handleSubmit(values, { props }) {
    props.setOnSuccess(true);

    const fullNameArray = values.fullName.split(' ');
    const lastIndex = fullNameArray.length - 1; // if they type in their full name as more than two words this will grab the last word as their lastname
    values['downloadUrl'] = props.downloadUrl || '';
    values['contentTitle'] = props.contentTitle || '';
    let ref_page = null;
    function zappierAPI() {
      API.zapier(values).then(() => mercuryFormSubmission());
    }

    if (windowCheck) {
      if (window.currentLocation === '/') {
        ref_page = 'homepage';
      } else {
        ref_page = window.currentLocation;
      }
    }

    {
      props.formType === 'requestInfo'
        ? zappierAPI()
        : API.hubspot(values, props.formType).then(() => {
          mercuryFormSubmission();
        });
    }

    const mercuryFormSubmission = () => {
      const mercuryAnnotations = {};
      const formFields = {};
      if (props.formType === 'contentDownload') {
        formFields['ref_employees'] = parseInt(values.ref_employees, 10);
        mercuryAnnotations['assetName'] = props.contentTitle;
        mercuryAnnotations['assetUrl'] = props.downloadUrl;
      } else if (props.formType === 'freeTimeCardCalculatorDownload') {
        mercuryAnnotations['assetUrl'] = props.downloadUrl;
      } else if (props.formType === 'requestInfo') {
        formFields['ref_employees'] = parseInt(values.ref_employees, 10);
        formFields['industry_id'] = values.industry_id;
        formFields['info_requested'] = values.message;
      } else if (props.formType === 'HWIcontentDownload') {
        mercuryAnnotations['assetName'] = props.contentTitle;
        mercuryAnnotations['assetUrl'] = props.downloadUrl;
      }
      // Mercury Tracking
      const params = () => parseParams();
      const attributes = {
        annotations: JSON.stringify(mercuryAnnotations),
        email: values.email,
        firstName: fullNameArray[0] || null,
        lastName: fullNameArray[lastIndex] || null,
        phoneNumber: values.phone || null,
        formId: props.formType,
        fields: JSON.stringify(formFields),
        cookieId: Cookies.get('user_attribution_id'),
        referralPage: ref_page,
        queryParams: params(),
      };

      trackMercuryEvent('marketing::formSubmissions', attributes);

      if (
        props.formType === 'contentDownload' ||
        props.formType === 'freeTimeCardCalculatorDownload'
      )
        gaTrackDownloads(values);
      if (props.formType === 'blogSubscription')
        gaTrackBlogSubscriptions(values);

      // eslint-disable-next-line no-undef
      Boolean(window.__adroll)
        ? __adroll.record_user({ adroll_segments: '4edfb558' })
        : console.log('ERROR: adroll record_user error');
      if (props.updateSuccess) props.updateSuccess(true);
    };
  },
})(CRMEmailSignupForm);

export default CRMForm;
